import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Modal, ModalBody, ModalFooter } from '@change/ds-core-ui/Modal';
import { Separator } from '@change/ds-core-ui/Separator';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { ChannelAwareCta } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { RoundShareIconButton, ShareChannelButton } from '#src/app/shared/components/share';

import { ChannelAwareCtaButton } from './components/ChannelAwareCtaButton';
import { PetitionShareSlidePetitionCard } from './components/PetitionShareSlideUpHeader';
import { PosterView } from './components/PosterView';
import { QrCodeTile } from './components/QrCodeTile';
import { QrCodeView } from './components/QrCodeView';
import { getStepHeader, renderHeader } from './components/StepHeader';
import {
	ACTION_BUTTON_TRACKING_DATA,
	CHANNEL_GRID_TRACKING_DATA,
	getShareIconOnBeforeShare,
	SHARE_ICON_SIZE_CLASSNAME,
} from './constants';
import { useQrCodeSteps } from './hooks';
import type { PetitionShareButtonConfig, PetitionShareSlideUpProps } from './types';
import { useScrollBodyToTopOnStep } from './useScrollBodyToTopOnStep';

const SpecialShareButtons = ({ configs }: { configs: readonly PetitionShareButtonConfig[] }) => (
	<div className="grid grid-cols-1 gap-x-0.5 gap-y-2" data-testid="petition-share-slide-up-special-buttons">
		{configs.map((buttonConfig) => (
			<ShareChannelButton
				key={buttonConfig.channel}
				buttonConfig={buttonConfig}
				trackingData={ACTION_BUTTON_TRACKING_DATA}
			/>
		))}
	</div>
);

/**
 * Splits the server-provided configs into the top "special" buttons and the remaining grid channels.
 * With a channel-aware CTA (productized per-country via the `psf_promote_or_share_channel_aware_cta`
 * FCM) a "Share on <channel>" button is promoted above the grid while the channel — and Copy link —
 * STAY in the grid. When no CTA renders (no channel-aware CTA, or its channel is missing from the share
 * config) Copy link is the primary special button, so the sheet still has a prominent action.
 */
function splitButtonConfigs(
	buttonConfigs: readonly PetitionShareButtonConfig[],
	channelAwareCta: ChannelAwareCta,
): {
	ctaButtonConfig: PetitionShareButtonConfig | undefined;
	specialButtonConfigs: PetitionShareButtonConfig[];
	regularButtonConfigs: PetitionShareButtonConfig[];
} {
	const ctaButtonConfig = channelAwareCta
		? buttonConfigs.find((config) => config.channel === channelAwareCta.channel)
		: undefined;
	const keepCopyInGrid = ctaButtonConfig !== undefined;
	const specialButtonConfigs: PetitionShareButtonConfig[] = [];
	const regularButtonConfigs = buttonConfigs.filter((config) => {
		if (config.channel === 'copy' && !keepCopyInGrid) {
			specialButtonConfigs.push(config);
			return false;
		}
		return true;
	});
	return { ctaButtonConfig, specialButtonConfigs, regularButtonConfigs };
}

const Footer = ({ continueButtonProps }: { continueButtonProps: PetitionShareSlideUpProps['continueButtonProps'] }) => {
	if (!continueButtonProps) return null;

	return (
		<ModalFooter className="justify-center" data-testid="petition-share-slide-up-footer" displayFooterTopSeparator>
			<Button
				data-qa="petition-share-slide-up-continue-button"
				variant="tertiary"
				className="w-full"
				onClick={continueButtonProps.action}
			>
				{continueButtonProps.label}
			</Button>
		</ModalFooter>
	);
};

/**
 * Extracted only to keep PetitionShareSlideUp's render within the max-lines-per-function budget.
 */
const ShareChannelsGrid = ({
	buttonConfigs,
	close,
	closeOnSocialShare,
	trailingTile,
}: {
	buttonConfigs: readonly PetitionShareButtonConfig[];
	close: () => void;
	closeOnSocialShare: boolean;
	/** A non-channel tile after the channel tiles. */
	trailingTile?: React.ReactNode;
}) => (
	<div className="grid grid-cols-4 gap-x-0.5 gap-y-5" data-testid="petition-share-slide-up-channels-grid">
		{buttonConfigs.map((buttonConfig) => (
			<RoundShareIconButton
				key={buttonConfig.channel}
				className={cn(SHARE_ICON_SIZE_CLASSNAME, 'w-full')}
				buttonConfig={buttonConfig}
				data-qa={`petition-share-slide-up-grid-${buttonConfig.channel}-button`}
				trackingData={CHANNEL_GRID_TRACKING_DATA}
				onBeforeShare={getShareIconOnBeforeShare({ channel: buttonConfig.channel, close, closeOnSocialShare })}
			/>
		))}
		{trailingTile}
	</div>
);

// eslint-disable-next-line max-lines-per-function
export const PetitionShareSlideUp: React.FC<PetitionShareSlideUpProps> = ({
	petition,
	buttonConfigs,
	onClose = () => {},
	onOpenChange,
	continueButtonProps,
	isOpen,
	shareVisual,
	closeOnSocialShare = true,
	channelAwareCta = null,
	qrCodeLayout,
	qrCode,
	qrCodeTracking,
	qrCodePoster,
	onQrCodeShared,
	cancelQrRedirect,
}) => {
	const { translate } = useI18n();
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	const {
		showQrTile,
		qrView,
		posterView,
		qrTileRef,
		openQrView,
		showGrid,
		openPosterView,
		showQrView,
		getExitReason,
		onQrCodeSaved,
		onPosterShared,
	} = useQrCodeSteps(isOpen, {
		qrCodeLayout,
		qrCode,
		qrCodeTracking,
		qrCodePoster,
		onQrCodeShared,
		cancelQrRedirect,
	});
	const isGrid = !qrView && !posterView;
	const isQrView = !!qrView;
	const isPosterView = !!posterView;
	const bodyRef = useScrollBodyToTopOnStep({ isQrView, isPosterView });
	const showsShareVisual = Boolean(shareVisual) && isGrid;
	const stepHeader = getStepHeader({
		isQrView,
		isPosterView,
		showGrid,
		showQrView,
	});

	const { ctaButtonConfig, specialButtonConfigs, regularButtonConfigs } = splitButtonConfigs(
		buttonConfigs,
		channelAwareCta,
	);

	return (
		<Modal
			isOpen={isOpen}
			transformToSheet
			onClose={onClose}
			onOpenChange={onOpenChange}
			aria-label={translate('corgi.psf.promote_or_share.share_slide_up.aria_label')}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			className="z-100"
		>
			{renderHeader({ shareVisual, stepHeader, translate })}

			{showsShareVisual && (
				<Separator className="border-stroke-weaker -mx-5 mt-4 mb-0 w-[calc(100%+2.5rem)] md:-mx-6 md:w-[calc(100%+3rem)]" />
			)}

			<ModalBody
				// The QR and poster views drop the grid's height cap, so the sheet grows to fit them (up to the
				// sheet's own full-screen cap) rather than scrolling.
				className={cn('pb-6', { 'max-h-[calc(85dvh-80px)]': isGrid, 'pt-0': showsShareVisual })}
				ref={bodyRef}
				data-testid="petition-share-slide-up-body"
			>
				{({ close }) => {
					if (qrView) {
						return (
							<QrCodeView
								qrCode={qrView.qrCode}
								petitionTitle={petition.ask}
								showPrint={qrView.showPrint}
								onOpenPoster={openPosterView}
								tracking={qrView.tracking}
								source={qrView.source}
								isSlideUpOpen={isOpen}
								getExitReason={getExitReason}
								onShared={onQrCodeSaved}
							/>
						);
					}
					if (posterView) {
						return (
							<PosterView
								petitionId={posterView.petitionId}
								petitionTitle={petition.ask}
								getTrackingData={posterView.getTrackingData}
								trackPrinted={posterView.trackPrinted}
								onShared={onPosterShared}
							/>
						);
					}
					return (
						<div className="w-full">
							{shareVisual ? (
								<img src={shareVisual} alt="Share Visual Preview" className="mx-auto my-4 block max-w-61 rounded-md" />
							) : (
								<div className="mb-4">
									<PetitionShareSlidePetitionCard petition={petition} />
								</div>
							)}
							{(ctaButtonConfig || specialButtonConfigs.length > 0) && (
								<>
									<div className="flex flex-col gap-4">
										{channelAwareCta && ctaButtonConfig && (
											<ChannelAwareCtaButton
												channel={channelAwareCta.channel}
												buttonConfig={ctaButtonConfig}
												onBeforeShare={getShareIconOnBeforeShare({
													channel: channelAwareCta.channel,
													close,
													closeOnSocialShare,
												})}
											/>
										)}
										{specialButtonConfigs.length > 0 && <SpecialShareButtons configs={specialButtonConfigs} />}
									</div>
									<Separator
										className="border-stroke-weaker my-5"
										data-testid="petition-share-slide-up-top-separator"
									/>
								</>
							)}
							<ShareChannelsGrid
								buttonConfigs={regularButtonConfigs}
								close={close}
								closeOnSocialShare={closeOnSocialShare}
								trailingTile={showQrTile && <QrCodeTile onPress={openQrView} tileRef={qrTileRef} />}
							/>
						</div>
					);
				}}
			</ModalBody>
			{isGrid && <Footer continueButtonProps={continueButtonProps} />}
		</Modal>
	);
};
